import React, { useState, useEffect } from "react";
import { Label } from "reactstrap";
import { TabPane, Row, Col } from "reactstrap";
import SiteContactView from "../../../../components/SiteContactView";
import CustomTextInput from "../../../../components/CustomTextInput";
import CustomDropdownInput from "../../../../components/CustomDropdownInput";
import SiteInBuildingRentalCalculationModal from "../SiteInBuildingRentalCalculationModal";
import SiteWholeRentalCalculationModal from "../SiteWholeRentalCalculationModal";
import SiteRevenueShareRentalCalculationModal from "../SiteRevenueShareRentalCalculationModal";
import { Button } from "shared";

import "./index.css";
import t from "utils/Lang";
import { getLayoutSize, getLayoutType } from "utils/SiteHelper";

interface RentalInformationTabProps {
  isView: boolean;
  isCreate: boolean;
  defaultStoreFormatId?: string | undefined | null;
  siteData: any;
  setSiteData: Function;
  statusOptions: any;
  customerGroupOptions: any;
  storeFormatOptions: any;
  storeTypeOptions: any;
  parkingSiteTypeOptions: any;
  externalEnvironmentFactorOptions: any;
  siteSurroundingAreaTypeOptions: any;
  sitePositionTypeOptions: any;
  siteId?: number;
  contractRentalTypeOptions: Array<any>;
  groups: Array<any>;
}

const calculationMethodData = [
  {
    value: "",
    label: "Chọn đơn vị tính tiền thuê/ Choose a rental unit",
  },
  {
    value: "whole",
    label: "Nguyên căn/ Whole",
  },
  {
    value: "area",
    label: "Theo m2/ By m2",
  },
  {
    value: "revenue_share",
    label: "Chia sẻ doanh thu/ Revenue sharing",
  },
];

const getCalculationMode = (mode: string) => {
  const data =
    calculationMethodData.find((dt) => dt.value == mode) ||
    calculationMethodData[1];
  return {
    rental_calculation_mode: {
      name: data.label,
      id: data.value,
    },
  };
};

export default function RentalInformationTab({
  isView,
  isCreate,
  defaultStoreFormatId,
  siteData,
  setSiteData,
  contractRentalTypeOptions,
  siteId,
  groups,
}: RentalInformationTabProps) {
  const [siteRentalCalculationMode, setSiteRentalCalculationMode] =
    useState<any>(getCalculationMode(siteData.data.rental_calculation_method));
  const [showCalculationModal, setShowCalculationModal] =
    useState<boolean>(false);

  const updateAttr = (attribute: string, value: any) => {
    if (attribute == "rental_calculation_mode") {
      selectRentalCalculationMethod(value.value);
      return;
    }
    let dataUpdate = {};
    if (typeof value === "object") {
      if (value?.label) {
        // Check value is object (value from dropdown menu)
        if (
          attribute == "is_new_contruction" ||
          attribute == "is_personal_contract"
        ) {
          dataUpdate = {
            [attribute]: value?.value,
          };
        } else {
          dataUpdate = {
            [attribute]: {
              id: value?.value,
              name: value?.label,
            },
          };
        }
      } else {
        dataUpdate = {
          [attribute]: null,
        };
      }
    } else {
      dataUpdate = { [attribute]: value };
    }
    setSiteData({
      ...siteData,
      ...{ data: { ...siteData?.data, ...dataUpdate } },
    });
  };

  const selectRentalCalculationMethod = (calculationMethod: string) => {
    setSiteData({
      ...siteData,
      data: {
        ...siteData.data,
        rental_calculation_method: calculationMethod,
      },
    });
    setShowCalculationModal(false);
  };

  const onSiteInBuildingCalculationComplete = (rentalCalculationData: any) => {
    updateAttr(
      "total_area",
      rentalCalculationData.indoor_area_sqm +
        rentalCalculationData.outdoor_area_sqm
    );
    updateAttr(
      "initial_lease_rate",
      rentalCalculationData.site_total_rental_fee
    );
    setShowCalculationModal(false);
  };

  const onSiteWholeCalculationComplete = (rentalCalculationData: any) => {
    updateAttr("initial_lease_rate", rentalCalculationData.initial_lease_rate);
    updateAttr("lease_term_year", rentalCalculationData.lease_term_year);
    updateAttr("lease_rate", rentalCalculationData.lease_rate);
    setShowCalculationModal(false);
  };

  const onShowModalButtonClick = () => {
    setShowCalculationModal(!showCalculationModal);
  };

  useEffect(() => {
    if (
      siteData?.data?.rental_calculation_method ||
      siteData?.data?.rental_calculation_method === ""
    ) {
      setSiteRentalCalculationMode(
        getCalculationMode(siteData?.data?.rental_calculation_method)
      );
    }
  }, [siteData?.data?.rental_calculation_method]);

  useEffect(() => {}, [showCalculationModal]);

  const showInBuildingCustomLayout =
    siteData.data?.store_format?.code === "inbuilding" ||
    (isCreate && defaultStoreFormatId === "2");

  const optionMapping: any = {
    contract_rental_type: contractRentalTypeOptions,
    is_personal_contract: [
      {
        value: true,
        label: "Cá nhân/ Personal",
      },
      {
        value: false,
        label: "Chủ đầu tư/ Investor",
      },
    ],
    rental_calculation_method: calculationMethodData,
    is_new_contruction: [
      {
        value: true,
        label: "Xây dựng mới 100%/ New construction",
      },
      {
        value: false,
        label: "Cải tạo/ Renovations",
      },
    ],
  };

  const uiLayoutDefinition: any = groups.map((g) => {
    const items = g.fields.map((f: any) => {
      const res: any = {
        attributeName: f.code,
        type: getLayoutType(f.field_data_type),
        size: getLayoutSize(f.code),
        isNumeric:
          f.field_data_type === "number" || f.field_data_type === "decimal",
        isDecimal: f.field_data_type === "decimal",
        labelText: f.name,
        options:
          f.field_data_type === "dropdown"
            ? optionMapping?.[f.code] || []
            : undefined,
      };
      if (f.code === "rental_calculation_method") {
        res.attributeName = "rental_calculation_mode";
        res.data = siteRentalCalculationMode;
        res.labelText = t().site_rental_information__select_calculator;
      }
      return res;
    });
    if (g.code === "rental_information_pricing") {
      items.splice(3, 0, {
        type: "custom",
        size: 6,
        component: !isCreate ? (
          <div style={{ marginTop: 20 }}>
            <span className="custom-attribute__text-input-label">
              {t().site_rental_information__calculator}
            </span>
            <Button
              style={{ paddingTop: 6, paddingBottom: 6 }}
              label={t().site_rental_information__detail_calculator}
              onClick={onShowModalButtonClick}
              type="primary"
              size="medium"
            />
          </div>
        ) : null,
      });
      items.splice(4, 0, {
        type: "custom",
        size: 8,
        displayCondition: showInBuildingCustomLayout,
        component: isCreate ? (
          <p style={styles.warning}>
            Lưu mặt bằng để hiển thị công cụ tính giá trong tòa nhà
          </p>
        ) : null,
      });
    }
    return {
      type: "row",
      label: g.name,
      items,
    };
  });

  return (
    <TabPane tabId="rental_info_tab" style={{}}>
      {uiLayoutDefinition.map((row: any, rowIndex: number) => (
        <div key={`row-index-${rowIndex}`}>
          <h4 className="site-summary__label-title">{row.label}</h4>
          <Row className="site-information-tab__section">
            {row.items.map((layoutItem: any, layoutIndex: number) => {
              if (layoutItem.type === "dropdown") {
                return (
                  <Col
                    key={`layout-${rowIndex}-${layoutIndex}`}
                    sm={{ size: layoutItem.size, offset: 0 }}
                  >
                    <CustomDropdownInput
                      disabled={layoutItem.isView || isView}
                      style={styles.customTextInput}
                      callback={updateAttr}
                      data={layoutItem?.data || siteData?.data}
                      options={layoutItem.options}
                      labels={layoutItem?.labelText ? null : siteData?.labels}
                      attributeName={layoutItem.attributeName}
                      labelText={layoutItem?.labelText}
                    />
                  </Col>
                );
              } else if (
                layoutItem.type === "text" ||
                layoutItem.type === "textarea" ||
                layoutItem.type === "date"
              ) {
                return (
                  <Col
                    key={`layout-index-${layoutIndex}`}
                    sm={{ size: layoutItem.size, offset: 0 }}
                  >
                    <CustomTextInput
                      isNumeric={layoutItem.isNumeric || false}
                      type={layoutItem.type}
                      disabled={isView}
                      style={styles.customTextInput}
                      callback={updateAttr}
                      data={siteData?.data}
                      labels={layoutItem?.labelText ? null : siteData?.labels}
                      attributeName={layoutItem.attributeName}
                      labelText={layoutItem?.labelText}
                    />
                  </Col>
                );
              } else if (layoutItem.type === "break") {
                return (
                  <Col
                    key={`layout-index-${layoutIndex}`}
                    sm={{ size: 6, offset: 0 }}
                  />
                );
              } else if (layoutItem.type === "custom") {
                return (
                  <Col
                    key={`layout-index-${layoutIndex}`}
                    sm={{ size: layoutItem.size, offset: 0 }}
                  >
                    {layoutItem.component}
                  </Col>
                );
              }
            })}
          </Row>
        </div>
      ))}
      <div>
        <h4 className="site-summary__label-title">
          {t().store_detail__contact}
        </h4>
        <div className="site-information-tab__section" style={{ padding: 15 }}>
          <SiteContactView locationId={siteData.data.id || siteId} isSite />
        </div>
      </div>
      {siteRentalCalculationMode.rental_calculation_mode.id === "area" &&
        showCalculationModal && (
          <SiteInBuildingRentalCalculationModal
            siteId={siteData.data.id || siteId}
            isView={isView}
            isShow={showCalculationModal}
            onSaveCallback={(rentalData: any) =>
              onSiteInBuildingCalculationComplete(rentalData)
            }
            onCancelCallback={() => setShowCalculationModal(false)}
          />
        )}
      {siteRentalCalculationMode.rental_calculation_mode.id === "whole" &&
        showCalculationModal && (
          <SiteWholeRentalCalculationModal
            siteId={siteData.data.id || siteId}
            isView={isView}
            isShow={showCalculationModal}
            onSaveCallback={(rentalData: any) =>
              onSiteWholeCalculationComplete(rentalData)
            }
            onCancelCallback={() => setShowCalculationModal(false)}
          />
        )}
      {siteRentalCalculationMode.rental_calculation_mode.id ===
        "revenue_share" &&
        showCalculationModal && (
          <SiteRevenueShareRentalCalculationModal
            siteId={siteData.data.id || siteId}
            isView={isView}
            isShow={showCalculationModal}
            onSaveCallback={() => setShowCalculationModal(false)}
            onCancelCallback={() => setShowCalculationModal(false)}
          />
        )}
    </TabPane>
  );
}

const styles = {
  customTextInput: {
    marginTop: 20,
  },
  contactView: {
    padding: 12,
  },
  warning: {
    fontSize: 12,
    color: "var(--danger)",
    fontWeight: 600,
  },
};
